import { useState, useRef, useCallback, useMemo } from 'react';
import { useFormacao, type FormacaoDocumento } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { supabase } from '@/lib/supabase';
import { estadoNormalizado } from '@/utils/estadoUtils';

const TIPOS = [
  { value: 'certificado',  label: 'Certificado',         icon: 'ri-medal-line',             bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { value: 'presenca',     label: 'Lista de Presenças',  icon: 'ri-contacts-book-line',     bg: 'bg-zinc-100',   text: 'text-zinc-600' },
  { value: 'inscricao',    label: 'Ficha de Inscrição',  icon: 'ri-user-add-line',          bg: 'bg-amber-50',   text: 'text-amber-700' },
  { value: 'programa',     label: 'Programa',            icon: 'ri-file-list-3-line',       bg: 'bg-zinc-100',   text: 'text-zinc-600' },
  { value: 'financeiro',   label: 'Financeiro',          icon: 'ri-money-euro-circle-line', bg: 'bg-yellow-50',  text: 'text-yellow-700' },
  { value: 'avaliacao',    label: 'Avaliação',           icon: 'ri-survey-line',            bg: 'bg-zinc-100',   text: 'text-zinc-600' },
  { value: 'regulamento',  label: 'Regulamento',         icon: 'ri-scales-line',            bg: 'bg-zinc-100',   text: 'text-zinc-600' },
  { value: 'outro',        label: 'Outro',               icon: 'ri-file-line',              bg: 'bg-zinc-100',   text: 'text-zinc-600' },
];

const tipoCfg = Object.fromEntries(TIPOS.map((t) => [t.value, t]));

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getAnoDocumento(doc: FormacaoDocumento, formacoes: { id: string; data_inicio: string | null }[]): number | null {
  const f = formacoes.find((fc) => fc.id === doc.formacao_id);
  if (f?.data_inicio) return new Date(f.data_inicio).getFullYear();
  if (doc.created_at) return new Date(doc.created_at).getFullYear();
  return null;
}

// ── Modal de Upload ───────────────────────────────────────────────────────────
interface UploadModalProps {
  formacoes: { id: string; nome: string; data_inicio: string | null }[];
  participantesPorFormacao: (id: string) => { id: string; nome: string }[];
  onClose: () => void;
  onUpload: (formacao_id: string, nome: string, tipo: string, file: File) => Promise<void>;
  uploading: boolean;
  preselectedFormacaoId?: string;
}

function UploadModal({ formacoes, participantesPorFormacao, onClose, onUpload, uploading, preselectedFormacaoId }: UploadModalProps) {
  const [formacaoId, setFormacaoId] = useState(preselectedFormacaoId ?? '');
  const [tipo, setTipo] = useState('certificado');
  const [nome, setNome] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(f: File) {
    setFile(f);
    if (!nome) setNome(f.name.replace(/\.[^.]+$/, ''));
    setErrors((prev) => ({ ...prev, file: '' }));
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!formacaoId) errs.formacaoId = 'Selecione uma ação de formação';
    if (!nome.trim()) errs.nome = 'Insira um nome para o documento';
    if (!file) errs.file = 'Selecione um ficheiro para carregar';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    await onUpload(formacaoId, nome.trim(), tipo, file!);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className="ri-upload-cloud-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Carregar Documento</h2>
              <p className="text-[11px] text-zinc-400">PDF, Word, Excel, imagens (máx. 10 MB)</p>
            </div>
          </div>
          <button onClick={onClose} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-4">
          {/* Ação de formação */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Ação de Formação <span className="text-rose-500">*</span>
            </label>
            <select
              value={formacaoId}
              onChange={(e) => { setFormacaoId(e.target.value); setErrors((p) => ({ ...p, formacaoId: '' })); }}
              className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.formacaoId ? 'border-rose-400' : 'border-zinc-200'}`}
            >
              <option value="">Selecionar ação...</option>
              {formacoes.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}{f.data_inicio ? ` (${new Date(f.data_inicio).getFullYear()})` : ''}
                </option>
              ))}
            </select>
            {errors.formacaoId && <p className="text-[11px] text-rose-500 mt-1">{errors.formacaoId}</p>}
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Tipo de Documento</label>
            <div className="flex flex-wrap gap-1.5">
              {TIPOS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTipo(t.value)}
                  className={`whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                    tipo === t.value ? `${t.bg} ${t.text} border-current` : 'text-zinc-400 bg-white border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <i className={`${t.icon} text-xs`} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Nome do Documento <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => { setNome(e.target.value); setErrors((p) => ({ ...p, nome: '' })); }}
              placeholder="Ex: Certificado — Excel Avançado — João Silva"
              className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.nome ? 'border-rose-400' : 'border-zinc-200'}`}
            />
            {errors.nome && <p className="text-[11px] text-rose-500 mt-1">{errors.nome}</p>}
          </div>

          {/* Ficheiro */}
          <div>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
            />
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 text-center cursor-pointer transition-all ${
                drag ? 'border-zinc-400 bg-zinc-50' : errors.file ? 'border-rose-300 bg-rose-50/30' : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/50'
              }`}
            >
              {file ? (
                <>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50">
                    <i className="ri-file-check-line text-xl text-emerald-600" />
                  </div>
                  <p className="text-[13px] font-semibold text-zinc-800">{file.name}</p>
                  <p className="text-[11px] text-zinc-400">{formatBytes(file.size)} · clica para substituir</p>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-zinc-100">
                    <i className="ri-upload-cloud-2-line text-xl text-zinc-400" />
                  </div>
                  <p className="text-[13px] font-semibold text-zinc-600">Arrasta ou clica para selecionar</p>
                  <p className="text-[11px] text-zinc-400">PDF, Word, Excel ou imagem · máx. 10 MB</p>
                </>
              )}
            </div>
            {errors.file && <p className="text-[11px] text-rose-500 mt-1">{errors.file}</p>}
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button type="button" onClick={onClose} className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A carregar...</>
              ) : (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-line text-sm" /></div>Carregar</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal Mover Documento ─────────────────────────────────────────────────────
interface MoverDocumentoModalProps {
  doc: FormacaoDocumento;
  formacoes: { id: string; nome: string; data_inicio: string | null }[];
  onClose: () => void;
  onMover: (docId: string, novaFormacaoId: string) => Promise<void>;
  saving: boolean;
}

function MoverDocumentoModal({ doc, formacoes, onClose, onMover, saving }: MoverDocumentoModalProps) {
  const [novaFormacaoId, setNovaFormacaoId] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!novaFormacaoId || novaFormacaoId === doc.formacao_id) return;
    await onMover(doc.id, novaFormacaoId);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-md mx-4">
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className="ri-arrow-left-right-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Mover Documento</h2>
              <p className="text-[11px] text-zinc-400 truncate max-w-[220px]">{doc.nome}</p>
            </div>
          </div>
          <button onClick={onClose} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Mover para a ação de formação <span className="text-rose-500">*</span>
            </label>
            <select
              value={novaFormacaoId}
              onChange={(e) => setNovaFormacaoId(e.target.value)}
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
            >
              <option value="">Selecionar ação de destino...</option>
              {formacoes
                .filter((f) => f.id !== doc.formacao_id)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}{f.data_inicio ? ` (${new Date(f.data_inicio).getFullYear()})` : ''}
                  </option>
                ))}
            </select>
            <p className="text-[10px] text-zinc-400 mt-1">O documento será associado à nova ação de formação.</p>
          </div>
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button type="button" onClick={onClose} className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 cursor-pointer">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !novaFormacaoId}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 cursor-pointer disabled:opacity-50"
            >
              {saving ? <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A mover...</> : 'Mover Documento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function DocumentosFormacaoPage() {
  const { documentos, formacoes, participantes, loading, saving, uploadDocumento, eliminarDocumento, getDocumentoUrl, refresh } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [formacaoFilter, setFormacaoFilter] = useState('');
  const [anoFilter, setAnoFilter] = useState('');
  const [viewMode, setViewMode] = useState<'lista' | 'acoes'>('acoes');
  const [showUpload, setShowUpload] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<FormacaoDocumento | null>(null);
  const [moverDoc, setMoverDoc] = useState<FormacaoDocumento | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [movingSaving, setMovingSaving] = useState(false);

  // Anos disponíveis nos documentos
  const anosDisponiveis = useMemo(() => {
    const set = new Set<number>();
    documentos.forEach((d) => {
      const ano = getAnoDocumento(d, formacoes);
      if (ano) set.add(ano);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [documentos, formacoes]);

  // Participantes por formação com nome preenchido
  const participantesPorFormacao = useCallback((formacaoId: string) => {
    return participantes
      .filter((p) => p.formacao_id === formacaoId)
      .map((p) => {
        const func = funcionarios.find((f) => f.id === p.funcionario_id);
        return { id: p.funcionario_id, nome: func?.nome ?? p.funcionario_nome ?? p.funcionario_id };
      })
      .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i);
  }, [participantes, funcionarios]);

  const filtered = useMemo(() => documentos.filter((d) => {
    const matchSearch = search === '' || d.nome.toLowerCase().includes(search.toLowerCase());
    const matchTipo = tipoFilter === '' || d.tipo === tipoFilter;
    const matchFormacao = formacaoFilter === '' || d.formacao_id === formacaoFilter;
    const ano = getAnoDocumento(d, formacoes);
    const matchAno = anoFilter === '' || (ano !== null && ano.toString() === anoFilter);
    return matchSearch && matchTipo && matchFormacao && matchAno;
  }), [documentos, search, tipoFilter, formacaoFilter, anoFilter, formacoes]);

  // Agrupado por ação
  const porAcao = useMemo(() => {
    const map: Record<string, { formacao: typeof formacoes[0]; docs: FormacaoDocumento[] }> = {};
    filtered.forEach((d) => {
      if (!map[d.formacao_id]) {
        const f = formacoes.find((fc) => fc.id === d.formacao_id);
        if (f) map[d.formacao_id] = { formacao: f, docs: [] };
      }
      if (map[d.formacao_id]) map[d.formacao_id].docs.push(d);
    });
    return Object.values(map).sort((a, b) => a.formacao.nome.localeCompare(b.formacao.nome));
  }, [filtered, formacoes]);

  const getFormacaoNome = useCallback((id: string) => formacoes.find((f) => f.id === id)?.nome ?? '—', [formacoes]);

  async function handleUpload(formacao_id: string, nome: string, tipo: string, file: File) {
    const result = await uploadDocumento({ formacao_id, nome, tipo, file });
    if (result) {
      setSuccessMsg(`"${nome}" carregado com sucesso!`);
      setTimeout(() => setSuccessMsg(''), 4000);
      // Forçar refresh para garantir que aparece na lista
      await refresh();
    } else {
      setSuccessMsg('Erro ao carregar documento. Tenta novamente.');
      setTimeout(() => setSuccessMsg(''), 4000);
    }
  }

  async function handleDelete(doc: FormacaoDocumento) {
    await eliminarDocumento(doc);
    setConfirmDelete(null);
    setSuccessMsg('Documento eliminado.');
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  async function handleMover(docId: string, novaFormacaoId: string) {
    setMovingSaving(true);
    try {
      const { error } = await supabase
        .from('formacao_documentos')
        .update({ formacao_id: novaFormacaoId })
        .eq('id', docId);
      if (!error) {
        await refresh();
        setSuccessMsg('Documento movido com sucesso!');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } finally {
      setMovingSaving(false);
    }
  }

  function handleDownload(doc: FormacaoDocumento) {
    if (!doc.url) return;
    window.open(getDocumentoUrl(doc.url), '_blank', 'noopener');
  }

  const totalCertificados = documentos.filter((d) => d.tipo === 'certificado').length;
  const totalInscricoes = documentos.filter((d) => d.tipo === 'inscricao').length;
  const acoesCom = new Set(documentos.map((d) => d.formacao_id)).size;

  function DocRow({ doc, i, showFormacao }: { doc: FormacaoDocumento; i: number; showFormacao?: boolean }) {
    const tc = tipoCfg[doc.tipo] ?? tipoCfg['outro'];
    const ano = getAnoDocumento(doc, formacoes);
    return (
      <tr className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${tc.bg}`}>
              <i className={`${tc.icon} text-base ${tc.text}`} />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-medium text-zinc-800 max-w-[280px] truncate" title={doc.nome}>{doc.nome}</p>
              {showFormacao && (
                <p className="text-[10px] text-zinc-400 truncate max-w-[240px]">{getFormacaoNome(doc.formacao_id)}</p>
              )}
            </div>
          </div>
        </td>
        <td className="px-5 py-3.5">
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>{tc.label}</span>
        </td>
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-zinc-500">
              {doc.created_at ? new Date(doc.created_at).toLocaleDateString('pt-PT') : '—'}
            </span>
            {ano && (
              <span className="text-[10px] font-bold bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{ano}</span>
            )}
          </div>
        </td>
        <td className="px-5 py-3.5">
          <span className="text-[12px] text-zinc-400">{formatBytes(doc.tamanho)}</span>
        </td>
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-1 justify-end">
            {doc.url && (
              <button
                onClick={() => handleDownload(doc)}
                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                title="Descarregar"
              >
                <i className="ri-download-line text-sm" />
              </button>
            )}
            <button
              onClick={() => setMoverDoc(doc)}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
              title="Mover para outra ação"
            >
              <i className="ri-arrow-left-right-line text-sm" />
            </button>
            {confirmDelete?.id === doc.id ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleDelete(doc)}
                  disabled={saving}
                  className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 px-2 py-1 rounded-lg cursor-pointer disabled:opacity-50"
                >
                  Eliminar
                </button>
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="whitespace-nowrap text-[11px] text-zinc-500 bg-zinc-100 hover:bg-zinc-200 px-2 py-1 rounded-lg cursor-pointer"
                >
                  Não
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(doc)}
                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-folder-open-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Documentos & Certificados</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Repositório de documentos — certificados, inscrições, presenças e mais</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Vista toggle */}
              <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('acoes')}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md transition-all cursor-pointer ${viewMode === 'acoes' ? 'bg-white text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-layout-masonry-line text-xs" /></div>
                  Por Ação
                </button>
                <button
                  onClick={() => setViewMode('lista')}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md transition-all cursor-pointer ${viewMode === 'lista' ? 'bg-white text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check text-xs" /></div>
                  Lista
                </button>
              </div>
              <button
                onClick={() => setShowUpload(true)}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-line text-sm" /></div>
                Carregar Documento
              </button>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {/* Banner de sucesso/erro */}
          {successMsg && (
            <div className={`flex items-center gap-3 border text-[13px] px-4 py-3 rounded-xl ${successMsg.includes('Erro') ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${successMsg.includes('Erro') ? 'ri-error-warning-fill' : 'ri-checkbox-circle-fill'} text-sm`} />
              </div>
              {successMsg}
            </div>
          )}

          {/* KPIs */}
          {!loading && documentos.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: 'ri-folder-2-line', value: documentos.length, label: 'Total de Documentos', bg: 'bg-zinc-100 text-zinc-600' },
                { icon: 'ri-medal-line', value: totalCertificados, label: 'Certificados', bg: 'bg-emerald-50 text-emerald-600' },
                { icon: 'ri-user-add-line', value: totalInscricoes, label: 'Fichas de Inscrição', bg: 'bg-amber-50 text-amber-600' },
                { icon: 'ri-book-open-line', value: acoesCom, label: 'Ações com Documentos', bg: 'bg-zinc-100 text-zinc-600' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <div>
                    <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                    <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                placeholder="Pesquisar documento..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
              />
            </div>
            <select
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
            >
              <option value="">Todos os tipos</option>
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select
              value={anoFilter}
              onChange={(e) => setAnoFilter(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
            >
              <option value="">Todos os anos</option>
              {anosDisponiveis.map((a) => <option key={a} value={a.toString()}>{a}</option>)}
            </select>
            <select
              value={formacaoFilter}
              onChange={(e) => setFormacaoFilter(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer max-w-[240px] truncate"
            >
              <option value="">Todas as ações</option>
              {formacoes.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
            <span className="text-[12px] text-zinc-400 ml-auto">
              <span className="font-semibold text-zinc-600">{filtered.length}</span> documento(s)
            </span>
          </div>

          {/* Conteúdo */}
          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
              </div>
              <p className="text-[13px] text-zinc-400">A carregar documentos...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-folder-open-line text-4xl text-zinc-200" />
              </div>
              <p className="text-[14px] font-semibold text-zinc-400">
                {documentos.length === 0 ? 'Ainda não há documentos carregados' : 'Nenhum documento encontrado'}
              </p>
              {documentos.length === 0 && (
                <button
                  onClick={() => setShowUpload(true)}
                  className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-line text-sm" /></div>
                  Carregar primeiro documento
                </button>
              )}
            </div>
          ) : viewMode === 'acoes' ? (
            /* Vista por ação */
            <div className="space-y-4">
              {porAcao.length === 0 ? (
                <div className="bg-white rounded-xl border border-zinc-100 py-12 flex flex-col items-center gap-2">
                  <i className="ri-search-line text-3xl text-zinc-200" />
                  <p className="text-[13px] text-zinc-400">Nenhum resultado</p>
                </div>
              ) : porAcao.map(({ formacao, docs }) => {
                const certCount = docs.filter((d) => d.tipo === 'certificado').length;
                const inscCount = docs.filter((d) => d.tipo === 'inscricao').length;
                const anoFormacao = formacao.data_inicio ? new Date(formacao.data_inicio).getFullYear() : null;
                return (
                  <div key={formacao.id} className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                    {/* Cabeçalho da ação */}
                    <div className="px-5 py-4 border-b border-zinc-50 flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                          <i className="ri-book-open-line text-zinc-600 text-base" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-[13px] font-bold text-zinc-900">{formacao.nome}</p>
                            {anoFormacao && (
                              <span className="text-[10px] font-bold bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{anoFormacao}</span>
                            )}
                          </div>
                          <p className="text-[11px] text-zinc-400">
                            {formacao.entidade_formadora ?? '—'}
                            {formacao.data_inicio ? ` · ${new Date(formacao.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {certCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-medal-line text-emerald-500 text-xs" />
                            </div>
                            <span className="text-[11px] font-semibold text-emerald-600">{certCount} cert.</span>
                          </div>
                        )}
                        {inscCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-user-add-line text-amber-500 text-xs" />
                            </div>
                            <span className="text-[11px] font-semibold text-amber-600">{inscCount} inscr.</span>
                          </div>
                        )}
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${estadoNormalizado(formacao.estado) === 'concluida' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                          {estadoNormalizado(formacao.estado) === 'concluida' ? 'Concluída' : 'Planeada'}
                        </span>
                        <span className="text-[11px] text-zinc-400">{docs.length} doc.</span>
                        <button
                          onClick={() => setShowUpload(true)}
                          className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                          title="Adicionar documento a esta ação"
                        >
                          <i className="ri-add-line text-sm" />
                        </button>
                      </div>
                    </div>
                    {/* Lista de documentos desta ação */}
                    <table className="w-full">
                      <tbody>
                        {docs.map((doc, i) => <DocRow key={doc.id} doc={doc} i={i} />)}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Vista lista */
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Documento</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Tipo</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Data / Ano</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Tamanho</th>
                    <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((doc, i) => <DocRow key={doc.id} doc={doc} i={i} showFormacao />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {showUpload && (
        <UploadModal
          formacoes={formacoes.map((f) => ({ id: f.id, nome: f.nome, data_inicio: f.data_inicio }))}
          participantesPorFormacao={participantesPorFormacao}
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
          uploading={saving}
        />
      )}

      {moverDoc && (
        <MoverDocumentoModal
          doc={moverDoc}
          formacoes={formacoes.map((f) => ({ id: f.id, nome: f.nome, data_inicio: f.data_inicio }))}
          onClose={() => setMoverDoc(null)}
          onMover={handleMover}
          saving={movingSaving}
        />
      )}
    </>
  );
}
